/** Browser-safe: the bot writes these links into notifications and the app parses them back. */

import type { z } from 'zod';
import { followInviteCodeSchema } from '../ontology/objects/analyst.ts';
import { inviteCodeSchema } from '../ontology/objects/member.ts';
import { AUTH_PATHS } from './wire/viewer.ts';

/** The console's routes: twelve pages and Help. A tab or section of one is linked by its name in `PAGES`. */
const ROUTES = [
  '/',
  '/trades',
  '/r/:id',
  '/analysts',
  '/analysts/:id',
  '/pnl',
  '/investments',
  '/markets',
  '/inbox',
  '/add',
  '/account',
  '/desk',
  '/help',
] as const;
export type RoutePath = (typeof ROUTES)[number];

/** The first tab of each is the bare route. */
export const MARKETS_TABS = ['calendar', 'news', 'participants', 'indicated'] as const;
export type MarketsTab = (typeof MARKETS_TABS)[number];
export const DESK_TABS = ['users', 'systems', 'ontology'] as const;
export type DeskTab = (typeof DESK_TABS)[number];
export const INVESTMENTS_TABS = ['overview', 'holdings', 'funds', 'inside', 'tax'] as const;
export type InvestmentsTab = (typeof INVESTMENTS_TABS)[number];

export const PAGES = {
  home: '/',
  trades: '/trades',
  recommendation: '/r',
  analysts: '/analysts',
  pnl: '/pnl',
  investments: '/investments',
  markets: '/markets',
  notifications: '/inbox',
  add: '/add',
  account: '/account',
  desk: '/desk',
  help: '/help',
  calendar: '/markets',
  news: '/markets/news',
  participants: '/markets/participants',
  indicated: '/markets/indicated',
  holdings: '/investments/holdings',
  funds: '/investments/funds',
  inside: '/investments/inside',
  tax: '/investments/tax',
  drafts: '/add#drafts',
  users: '/desk',
  systems: '/desk/systems',
  ontology: '/desk/ontology',
} as const;

// Every row link below stays on `/`, which the console opens as Trades with the query kept:
// the roll sweep knows a roll-due note it already sent by its `href`, so a move would resend it.

export const recommendationLink = (recommendationId: number): string =>
  `/?recommendation=${recommendationId}`;

export const ticketLink = (recommendationId: number): string =>
  `/?recommendation=${recommendationId}&ticket=take`;

/** Lands on the holding's row; a fill no longer held lands nowhere, so link its recommendation. */
export const positionLink = (positionId: number): string => `/?position=${positionId}`;

export const exitLink = (positionId: number, price: number | null): string =>
  `${positionLink(positionId)}&ticket=exit${price === null ? '' : `&price=${price}`}`;

export const rolloverLink = (positionId: number): string =>
  `${positionLink(positionId)}&ticket=rollover`;

/** Where a position's roll-due note points, and so how the sweep finds it said. */
export const rollDueLink = (positionId: number, deskRolls: boolean): string =>
  deskRolls ? rolloverLink(positionId) : positionLink(positionId);

export const recommendationDetailLink = (recommendationId: number): string =>
  `${PAGES.recommendation}/${recommendationId}`;

/** The history is a section of the recommendation's page. */
export const historyLink = (recommendationId: number): string =>
  `${recommendationDetailLink(recommendationId)}#history`;

const idOf = (raw: string | undefined): number | null => {
  const id = Number(raw);
  return raw !== undefined && Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const recommendationOfPath = (path: string): number | null =>
  idOf(/^\/r\/(\d+)$/.exec(path)?.[1]);

/**
 * Tabs live under /analysts so the header's prefix match lights that section. Who may
 * read a tab is the page's call; it answers not-found, never forbidden.
 */
const ANALYST_TABS = [
  'overview',
  'recommendations',
  'channel',
  'followers',
  'books',
  'settings',
] as const;
export type AnalystTab = (typeof ANALYST_TABS)[number];

export function analystLink(
  analystId: number,
  tab: AnalystTab = 'overview',
  symbol: string | null = null,
): string {
  const path = `${PAGES.analysts}/${analystId}${tab === 'overview' ? '' : `/${tab}`}`;
  return symbol === null ? path : `${path}?${new URLSearchParams({ symbol })}`;
}

/** The first tab is the bare base, so a path naming it, or any unknown tab, is no page. */
function tabOf<T extends string>(tabs: readonly T[], named: string | undefined): T | null {
  if (named === undefined) return tabs[0] ?? null;
  const tab = tabs.find((one) => one === named);
  return tab === undefined || tab === tabs[0] ? null : tab;
}

function analystOfPath(path: string): { id: number; tab: AnalystTab } | null {
  const match = /^\/analysts\/(\d+)(?:\/([a-z]+))?$/.exec(path);
  const id = idOf(match?.[1]);
  const tab = match ? tabOf(ANALYST_TABS, match[2]) : null;
  return id === null || tab === null ? null : { id, tab };
}

type PlainRoute = Exclude<
  RoutePath,
  '/r/:id' | '/analysts/:id' | '/investments' | '/markets' | '/desk'
>;

export type RouteAt =
  | { route: PlainRoute }
  | { route: '/r/:id'; id: number }
  | { route: '/analysts/:id'; id: number; tab: AnalystTab }
  | { route: '/investments'; tab: InvestmentsTab }
  | { route: '/markets'; tab: MarketsTab }
  | { route: '/desk'; tab: DeskTab };

const PLAIN: readonly string[] = [
  '/',
  '/trades',
  '/analysts',
  '/pnl',
  '/inbox',
  '/add',
  '/account',
  '/help',
] satisfies PlainRoute[];

/**
 * Shared by the server's status codes and the app's route table, so they agree. A trailing slash
 * is no page. `/` is Home, or the landing page to somebody signed out.
 */
export function routeOf(path: string): RouteAt | null {
  const recommendation = recommendationOfPath(path);
  if (recommendation !== null) return { route: '/r/:id', id: recommendation };
  const analyst = analystOfPath(path);
  if (analyst !== null) return { route: '/analysts/:id', ...analyst };
  const tabbed = /^\/(investments|markets|desk)(?:\/([a-z]+))?$/.exec(path);
  if (tabbed?.[1] === 'investments') {
    const tab = tabOf(INVESTMENTS_TABS, tabbed[2]);
    return tab === null ? null : { route: '/investments', tab };
  }
  if (tabbed?.[1] === 'markets') {
    const tab = tabOf(MARKETS_TABS, tabbed[2]);
    return tab === null ? null : { route: '/markets', tab };
  }
  if (tabbed?.[1] === 'desk') {
    const tab = tabOf(DESK_TABS, tabbed[2]);
    return tab === null ? null : { route: '/desk', tab };
  }
  return PLAIN.includes(path) ? { route: path as PlainRoute } : null;
}

/** Outside the console: anybody may read them, signed in or out, as Google's consent screen links them. */
export const LEGAL = { privacy: '/privacy', terms: '/terms' } as const;
export type LegalPage = keyof typeof LEGAL;

export const legalOf = (path: string): LegalPage | null =>
  path === LEGAL.privacy ? 'privacy' : path === LEGAL.terms ? 'terms' : null;

export const isKnownPage = (path: string): boolean =>
  routeOf(path) !== null || legalOf(path) !== null;

/** The one origin search engines may index; staging and local runs ask them not to. */
export const SITE_ORIGIN = 'https://alphadesk.vaari.dev';

/** What a crawler may read: the landing page and the legal pages, each prerendered by `vite build`. */
export const PUBLIC_PAGES = ['/', ...Object.values(LEGAL)];

/** A member's own broker login, which comes back to `next`, a path on this site, connected. */
export const brokerLoginLink = (next: string): string =>
  `${AUTH_PATHS.broker}?${new URLSearchParams({ next })}`;

/** A link that carries one code under `/<at>/`, and the code read back off a path. */
function codeLinks(at: string, schema: z.ZodType<string>) {
  const path = new RegExp(`^/${at}/([^/]+)/?$`);
  return {
    link: (code: string): string => `/${at}/${code}`,
    of: (from: string): string | null => {
      const code = schema.safeParse(path.exec(from)?.[1]);
      return code.success ? code.data : null;
    },
  };
}

/** A member's invite, short enough to read aloud; the server answers it, never the app. */
export const { link: inviteLink, of: inviteOfPath } = codeLinks('i', inviteCodeSchema);

/** An analyst's follow invite, for one person; the server answers it, never the app. */
export const { link: followInviteLink, of: followInviteOfPath } = codeLinks(
  'join',
  followInviteCodeSchema,
);

/** A whole path, ending where a query, a hash or nothing follows. */
const page = (from: string): RegExp => new RegExp(`^${from}\\/?(?=[?#]|$)`);
/** A path whose new home is a section: the hash is the section's, the query is kept. */
const section = (from: string): RegExp => new RegExp(`^${from}\\/?(\\?[^#]*)?(?:#.*)?$`);
/** The overview's address, old or new, with a hash since moved onto a tab. */
const onInvestments = (ids: string): RegExp =>
  new RegExp(`^(?:/portfolio|${PAGES.investments})\\/?(\\?[^#]*)?#(${ids})$`);

/**
 * Addresses of pages since renamed or merged, still in sent messages, the inbox and bookmarks.
 * The server redirects them, and the app reads them so before it draws.
 */
const RENAMED: readonly [RegExp, string][] = [
  [page('/feed'), PAGES.systems],
  // Whose listing /profile was is not in the address; the directory leads a reader to their own.
  [page('/profile'), PAGES.analysts],
  // Holdings is gone; what the desk placed is on Trades, which was the board.
  [page('/holdings'), PAGES.trades],
  [page('/board'), PAGES.trades],
  [page('/now'), PAGES.trades],
  [page('/reco/(\\d+)'), `${PAGES.recommendation}/$1`],
  [section('/recommendation/(\\d+)/history'), `${PAGES.recommendation}/$1$2#history`],
  [page('/recommendation/(\\d+)'), `${PAGES.recommendation}/$1`],
  [page('/home'), PAGES.home],
  [page('/notifications'), PAGES.notifications],
  [section('/drafts'), `${PAGES.add}$1#drafts`],
  [page('/users'), PAGES.users],
  [page('/systems'), PAGES.systems],
  [page('/ontology'), PAGES.ontology],
  [page('/calendar'), PAGES.calendar],
  [page('/participants'), PAGES.participants],
  // The overview's sections moved onto its tabs; a sent note still links `/portfolio#changes`.
  [onInvestments('stocks|stock-[^#]*|sectors|sizes|changes'), `${PAGES.inside}$1#$2`],
  [onInvestments('overlap'), `${PAGES.funds}$1#$2`],
  [onInvestments('holdings'), `${PAGES.holdings}$1#$2`],
  // Portfolio is Investments; the server sees no hash, so the browser carries it here to be read above.
  [page('/portfolio/(holdings|funds|inside|tax)'), `${PAGES.investments}/$1`],
  [page('/portfolio'), PAGES.investments],
  [/([?&])reco(s?)=/, '$1recommendation$2='],
];

export function renamedHref(href: string): string | null {
  const renamed = RENAMED.reduce((to, [from, into]) => to.replace(from, into), href);
  return renamed === href ? null : renamed;
}
